import { useEffect, useState } from 'react';
import type {
  PlanCategory,
  PlanOrigin,
  PlanRequirementType,
  PlanReviewStatus,
  ProductionDepartment,
  ProductionRequirementView,
  ResourceType,
} from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { useRefusal } from '../../lib/permissions.js';
import { EmptyState } from '../ui/EmptyState.js';
import { SegmentedFilter, type FilterOption } from '../ui/SegmentedFilter.js';
import { usePlan, usePlanWrite, writeError } from './context.js';
import {
  DEPARTMENT_LABELS,
  ORIGIN_LABELS,
  PLAN_CATEGORIES,
  REQUIREMENT_TYPES,
  REQUIREMENT_TYPE_LABELS,
  RESOURCE_LABELS,
  RESOURCE_TYPES,
  REVIEW_FILTER_LABELS,
  categoryLabel,
} from './labels.js';
import { IncompleteBanner, PlanLoadState, sceneRequirements } from './PlanParts.js';
import { RequirementRow } from './RequirementRow.js';

const ORIGINS: readonly PlanOrigin[] = ['EXPLICIT', 'DERIVED', 'SUGGESTED', 'MANUAL'];
const REVIEW_STATES: readonly PlanReviewStatus[] = ['PENDING', 'ACCEPTED', 'REJECTED', 'NEEDS_REVALIDATION'];

type All = 'ALL';

/**
 * WHAT THIS SCENE NEEDS, BEYOND WHAT THE TEXT TAGS — under the scene card on the
 * Breakdown lens.
 *
 * Every requirement of the plan whose scenes include this one: what the
 * screenplay states, what the plan reasoned from it, what it suggests, and what
 * a person added. Grouped by the sheet's categories; narrowed by origin, by
 * decision and by department, each with its count. A link from the plan's
 * overview (`?req=`) clears the filters that would hide the row it points at,
 * and the row scrolls into view.
 */
export function DerivedRequirementsPanel({ sceneId }: { sceneId: string }) {
  const { plan, focusRequirementId } = usePlan();
  const [origin, setOrigin] = useState<PlanOrigin | All>('ALL');
  const [review, setReview] = useState<PlanReviewStatus | All>('ALL');
  const [department, setDepartment] = useState<ProductionDepartment | All>('ALL');
  const [adding, setAdding] = useState(false);
  const refusal = useRefusal('SCRIPT_REVIEW');

  // A pointed-at row is never filtered away.
  useEffect(() => {
    if (focusRequirementId === null) return;
    setOrigin('ALL');
    setReview('ALL');
    setDepartment('ALL');
  }, [focusRequirementId, sceneId]);

  if (plan === null) {
    return (
      <section className="ui-panel pp-panel" data-testid="production-derived">
        <div className="ui-panel-body">
          <PlanLoadState testId="production-derived" />
        </div>
      </section>
    );
  }

  const all = sceneRequirements(plan, sceneId);
  const shown = all.filter(
    (r) =>
      (origin === 'ALL' || r.origin === origin) &&
      (review === 'ALL' || r.reviewStatus === review) &&
      (department === 'ALL' || r.departments.some((d) => d.department === department)),
  );
  const departments = [...new Set(all.flatMap((r) => r.departments.map((d) => d.department)))];

  const groups = new Map<PlanCategory | null, ProductionRequirementView[]>();
  for (const category of [...PLAN_CATEGORIES, null]) {
    const rows = shown.filter((r) => r.primaryCategory === category);
    if (rows.length > 0) groups.set(category, rows);
  }

  return (
    <section className="ui-panel pp-panel" data-testid="production-derived" aria-labelledby="production-derived-title">
      <div className="ui-panel-head">
        <h3 className="ui-panel-title" id="production-derived-title">
          What this scene needs
        </h3>
        <button
          type="button"
          className="ctl"
          data-testid="production-add-requirement"
          aria-expanded={adding}
          disabled={refusal !== null}
          title={refusal ?? undefined}
          onClick={() => {
            setAdding((on) => !on);
          }}
        >
          Add a requirement
        </button>
      </div>
      <div className="ui-panel-body pp-stack">
        <IncompleteBanner plan={plan} />

        {adding ? (
          <AddRequirementForm
            sceneId={sceneId}
            onDone={() => {
              setAdding(false);
            }}
          />
        ) : null}

        {all.length === 0 ? (
          <EmptyState title="Nothing in the plan for this scene" testId="production-derived-empty">
            The plan has no requirement on this scene. Add one if the production needs something here.
          </EmptyState>
        ) : (
          <>
            <div className="pp-filters">
              <SegmentedFilter
                label="Where it comes from"
                testIdPrefix="production-filter-origin"
                value={origin}
                onChange={setOrigin}
                options={[
                  { id: 'ALL', label: 'All', count: all.length },
                  ...ORIGINS.map(
                    (o): FilterOption<PlanOrigin> => ({ id: o, label: ORIGIN_LABELS[o], count: all.filter((r) => r.origin === o).length }),
                  ),
                ]}
              />
              <SegmentedFilter
                label="Decision"
                testIdPrefix="production-filter-review"
                value={review}
                onChange={setReview}
                options={[
                  { id: 'ALL', label: 'All decisions', count: all.length },
                  ...REVIEW_STATES.map(
                    (s): FilterOption<PlanReviewStatus> => ({
                      id: s,
                      label: REVIEW_FILTER_LABELS[s],
                      count: all.filter((r) => r.reviewStatus === s).length,
                      tone: s === 'NEEDS_REVALIDATION' || s === 'PENDING' ? 'attention' : undefined,
                    }),
                  ),
                ]}
              />
              {departments.length === 0 ? null : (
                <SegmentedFilter
                  label="Department"
                  testIdPrefix="production-filter-department"
                  value={department}
                  onChange={setDepartment}
                  options={[
                    { id: 'ALL', label: 'All departments' },
                    ...departments.map(
                      (d): FilterOption<ProductionDepartment> => ({
                        id: d,
                        label: DEPARTMENT_LABELS[d],
                        count: all.filter((r) => r.departments.some((t) => t.department === d)).length,
                      }),
                    ),
                  ]}
                />
              )}
            </div>

            {shown.length === 0 ? (
              <p className="mm pp-note" data-testid="production-derived-filtered-empty">
                Nothing matches these filters.
              </p>
            ) : (
              [...groups.entries()].map(([category, rows]) => (
                <div key={category ?? 'none'} className="pp-group" data-testid={`production-group-${category ?? 'NONE'}`}>
                  <h4 className="pp-group-title">{`${categoryLabel(category)} · ${String(rows.length)}`}</h4>
                  <ul className="pp-list">
                    {rows.map((r) => (
                      <RequirementRow key={r.id} requirement={r} sceneId={sceneId} pinned={r.requirementType === 'SAFETY_REVIEW'} />
                    ))}
                  </ul>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </section>
  );
}

/** A requirement somebody adds by hand, on this scene. It has no rule behind it (MANUAL). */
function AddRequirementForm({ sceneId, onDone }: { sceneId: string; onDone: () => void }) {
  const { projectId, scriptVersionId } = usePlan();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<PlanRequirementType>('ASSET');
  const [category, setCategory] = useState<PlanCategory | ''>('');
  const [resource, setResource] = useState<ResourceType | ''>('');
  const [count, setCount] = useState('');

  const add = usePlanWrite(() =>
    api.addPlanRequirement(projectId, {
      scriptVersionId,
      name: name.trim(),
      description: description.trim(),
      requirementType: type,
      primaryCategory: category === '' ? null : category,
      departments: [],
      resourceType: resource === '' ? null : resource,
      sceneIds: [sceneId],
      approvedQuantity: count.trim() === '' ? null : Number(count),
    }),
  );

  const parsedCount = count.trim() === '' ? null : Number(count);
  const countValid = parsedCount === null || (Number.isInteger(parsedCount) && parsedCount >= 1 && parsedCount <= 9999);
  const valid = name.trim() !== '' && countValid;

  return (
    <form
      className="pp-form"
      data-testid="production-add-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        add.mutate(undefined, { onSuccess: onDone });
      }}
    >
      <label className="pp-field">
        <span>Name</span>
        <input
          value={name}
          maxLength={255}
          required
          data-testid="production-add-name"
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </label>
      <label className="pp-field">
        <span>Type</span>
        <select
          value={type}
          data-testid="production-add-type"
          onChange={(e) => {
            setType(REQUIREMENT_TYPES.find((t) => t === e.target.value) ?? 'ASSET');
          }}
        >
          {REQUIREMENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {REQUIREMENT_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </label>
      <label className="pp-field">
        <span>Category</span>
        <select
          value={category}
          data-testid="production-add-category"
          onChange={(e) => {
            setCategory(PLAN_CATEGORIES.find((c) => c === e.target.value) ?? '');
          }}
        >
          <option value="">No category</option>
          {PLAN_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {categoryLabel(c)}
            </option>
          ))}
        </select>
      </label>
      <label className="pp-field">
        <span>Resource to find</span>
        <select
          value={resource}
          data-testid="production-add-resource"
          onChange={(e) => {
            setResource(RESOURCE_TYPES.find((t) => t === e.target.value) ?? '');
          }}
        >
          <option value="">None</option>
          {RESOURCE_TYPES.map((t) => (
            <option key={t} value={t}>
              {RESOURCE_LABELS[t]}
            </option>
          ))}
        </select>
      </label>
      <label className="pp-field">
        <span>Approved count</span>
        <input
          type="number"
          min={1}
          max={9999}
          step={1}
          inputMode="numeric"
          value={count}
          placeholder="Leave empty if not known"
          aria-invalid={!countValid}
          data-testid="production-add-count"
          onChange={(e) => {
            setCount(e.target.value);
          }}
        />
      </label>
      <label className="pp-field pp-field-wide">
        <span>Description</span>
        <textarea
          value={description}
          maxLength={1000}
          rows={2}
          data-testid="production-add-description"
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
      </label>
      <div className="ui-toolbar">
        <button type="submit" className="ctl" disabled={!valid || add.isPending} data-testid="production-add-save">
          {add.isPending ? 'Adding…' : 'Add to this scene'}
        </button>
        <button type="button" className="ctl" onClick={onDone} data-testid="production-add-cancel">
          Cancel
        </button>
      </div>
      {add.error === null ? null : (
        <p className="pp-error" role="alert" data-testid="production-add-error">
          {writeError(add.error)}
        </p>
      )}
    </form>
  );
}
